// Maps the backend geo-zone API (/api/v1/geo-zones, see crm-core GeoZoneResponse) to the shape
// the Zones UI consumes. `boundaryDefinitions` is the per-boundary source of truth
// (name/geoId/source/geometry) used by the editor; `boundary` is a coordinate-level union derived
// from those definitions for thumbnails and read-only map overlays.

import { unionForDisplay } from 'components/zones/zone-boundary-map/boundaryGeoJson'

import type { BoundaryDefinition } from 'components/zones/zone-boundary-map/boundaryGeoJson'
import type { CreateGeoZoneRequestApi, GeoZoneListResponseApi, GeoZoneResponseApi, UpdateGeoZoneRequestApi } from 'types/api/zones/zone'
import type { GeoZoneMatchResponseApi } from 'types/api/zones/zoneMatch'
import type { Zone, ZoneFormValues, ZoneListPage, ZoneMatch } from 'types/modules/zones/zone'

const toContent = (res: GeoZoneListResponseApi | undefined): GeoZoneResponseApi[] => {
  if (Array.isArray(res)) return res
  return res?.content ?? []
}

const toBoundaryDefinitions = (definitions: unknown): BoundaryDefinition[] =>
  Array.isArray(definitions) ? (definitions as BoundaryDefinition[]) : []

export const fromZoneApi = (zone: GeoZoneResponseApi | null | undefined): Zone | null => {
  if (!zone) return null

  const boundaryDefinitions = toBoundaryDefinitions(zone.boundaryDefinitions)
  return {
    id: zone.id,
    name: zone.name,
    description: zone.description ?? '',
    isDefault: zone.isDefault ?? false,
    isEligibilityZone: zone.isEligibilityZone ?? false,
    isTaxZone: zone.isTaxZone ?? false,
    isPricingZone: zone.isPricingZone ?? false,
    boundaryDefinitions,
    boundary: unionForDisplay(boundaryDefinitions),
    linkedTaxes: zone.linkedTaxes ?? [],
    taxCount: zone.taxCount ?? 0,
    accountCount: zone.accountCount ?? 0,
    locationCount: zone.locationCount ?? 0,
    archivedAt: zone.archivedAt ?? null,
    createdAt: zone.createdAt ?? null,
    updatedAt: zone.updatedAt ?? null,
  }
}

export const fromZoneListApi = (res: GeoZoneListResponseApi | undefined): ZoneListPage => {
  const content = toContent(res)
    .map(fromZoneApi)
    .filter((zone): zone is Zone => zone !== null)
  const page = Array.isArray(res) ? undefined : res
  return {
    content,
    totalElements: page?.totalElements ?? content.length,
    totalPages: page?.totalPages ?? 1,
    size: page?.size ?? content.length,
    number: page?.number ?? 0,
  }
}

export const fromZoneMatchApi = (match: GeoZoneMatchResponseApi): ZoneMatch => ({
  id: match.id,
  name: match.name,
  isEligibilityZone: match.isEligibilityZone ?? false,
  isTaxZone: match.isTaxZone ?? false,
  isPricingZone: match.isPricingZone ?? false,
})

const trimOrUndefined = (value: string | undefined) => value?.trim() || undefined

/** Roles are optional here because the adapter coerces them — the form itself always sends all three. */
type ZoneRequestInput = Partial<ZoneFormValues> & { name: string }

// UI -> backend CreateGeoZoneRequest (name required, description optional). The three role
// booleans gate where the zone surfaces (eligibility/tax/pricing pickers).
export const toCreateZoneRequest = (data: ZoneRequestInput): CreateGeoZoneRequestApi => ({
  name: data.name?.trim(),
  description: trimOrUndefined(data.description),
  isEligibilityZone: Boolean(data.isEligibilityZone),
  isTaxZone: Boolean(data.isTaxZone),
  isPricingZone: Boolean(data.isPricingZone),
})

// UI -> backend UpdateGeoZoneRequest. isDefault is omitted when not provided. The edit drawer
// sends all three role booleans so purpose changes persist.
export const toUpdateZoneRequest = (data: ZoneRequestInput): UpdateGeoZoneRequestApi => ({
  ...toCreateZoneRequest(data),
  isDefault: data.isDefault,
})
